/**
 * The top ten, in one place.
 *
 * The cap used to be the number twenty written into a database trigger and
 * three separate client files, which is why one of those files hardcoded
 * is_priority and threw an unhandled error the moment a list filled up, and
 * why another told people "card added to More" after an insert that had
 * actually failed. The number and the words that describe it now come from
 * here, so the next time the cap moves it moves once.
 *
 * The database still enforces it. This module exists so the app can say the
 * same thing the trigger says before the trigger has to say it.
 */

import { supabase } from '../lib/supabase';

/** How many cards the ranked grid holds. Five across, two down. */
export const TOP_TEN_LIMIT = 10;

/**
 * The Postgres error code the cap raises. Distinguishing it from a generic
 * failure is the difference between "your ten are taken" and "something went
 * wrong", and the old code could not tell them apart.
 */
const CAP_ERROR_CODE = '23514';

/** What to call the list in front of a person. */
export const TOP_TEN_LABEL = 'Top 10';

/** Said when there is no room left. */
export const topTenFullMessage = () => `Your ${TOP_TEN_LABEL} is full. Remove one to add another.`;

/** Said on the counter bar, as a fraction so the remaining room is visible. */
export const topTenCounterLabel = (count) => `${TOP_TEN_LABEL}: ${count ?? 0}/${TOP_TEN_LIMIT}`;

/** Said on a button that cannot be tapped. */
export const topTenFullLabel = () => `${TOP_TEN_LABEL} Full`;

export const isTopTenFull = (priorityCount) => (priorityCount ?? 0) >= TOP_TEN_LIMIT;

/**
 * Whether an error thrown by a wish list write was the cap and not a fault.
 *
 * Matched on the code first, because the message is the trigger's to change.
 * The text fallback covers a row written before this release.
 */
export function isTopTenCapError(error) {
  if (!error) return false;
  if (error.code === CAP_ERROR_CODE) return true;
  const message = error.message || '';
  return /top 10 is full|top 20 limit/i.test(message);
}

/**
 * How many of a person's wish cards are in the ranked ten.
 *
 * Counted against is_priority rather than against the whole list. The old
 * callers counted every wish row and compared it to the cap, so someone with
 * ten cards outside the ten was told their ten was full.
 */
export async function countTopTen(userId) {
  const { count, error } = await supabase
    .from('wish_list')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_priority', true);
  if (error) throw error;
  return count || 0;
}

/**
 * Read the ranked ten in slot order.
 *
 * Ordered by position, which the database hands out, so an empty slot is a
 * gap in the numbers rather than something the client has to infer.
 */
export async function fetchTopTen(userId) {
  const { data, error } = await supabase
    .from('wish_list')
    .select('*')
    .eq('user_id', userId)
    .eq('is_priority', true)
    .order('position', { ascending: true });
  if (error) throw error;
  return data || [];
}

/**
 * Put a card in the ranked ten, or say why not.
 *
 * The slot is not passed: the database picks the lowest free one, so two
 * devices adding at once cannot land on the same square. Resolves to
 * { added, full, error } rather than throwing, because every caller here wants
 * to show a message rather than break.
 */
export async function addToTopTen(userId, card) {
  const { error } = await supabase
    .from('wish_list')
    .insert({
      user_id: userId,
      tcgdex_card_id: card.tcgdexCardId,
      card_name: card.cardName,
      set_id: card.setId,
      set_name: card.setName,
      variant: card.variant || 'Normal',
      image_url: card.imageUrl || null,
      is_priority: true,
    });

  if (!error) return { added: true, full: false, error: null };
  if (isTopTenCapError(error)) return { added: false, full: true, error: null };
  return { added: false, full: false, error };
}

/**
 * Free a slot. The only removal the celebration offers, because master sets
 * and wish binders are never emptied - they fill in.
 */
export async function removeFromTopTen(wishId, profileId) {
  const { data, error } = await supabase.rpc('wish_remove_from_top_ten', {
    p_wish_id: wishId,
    ...(profileId ? { p_as: profileId } : {}),
  });
  if (error) throw error;
  return data;
}

/**
 * Rewrite the whole grid in one call.
 *
 * Two cards swapping squares cannot be two updates: the unique index on the
 * slot is checked per row, so the halfway state collides. The server clears
 * the numbers and writes them again inside one transaction.
 *
 * @param {Object} order  { [wishListRowId]: slot }
 */
export async function reorderTopTen(order, profileId) {
  const { data, error } = await supabase.rpc('wish_reorder_top_ten', {
    p_order: order,
    ...(profileId ? { p_as: profileId } : {}),
  });
  if (error) throw error;
  return data;
}
